import { useEffect, useRef, useState } from 'react';
import { GoogleIcon } from '../assets/icons';

/** Minimal typing for the Google Identity Services (GSI) client we use. */
interface CredentialResponse {
  credential?: string;
}
interface GoogleAccountsId {
  initialize: (config: {
    client_id: string;
    callback: (res: CredentialResponse) => void;
  }) => void;
  renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
}
declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
  }
}

const GSI_SRC = 'https://accounts.google.com/gsi/client';
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

/** Load the GSI script once and resolve when `window.google` is ready. */
function loadGsi(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google script')));
      return;
    }
    const s = document.createElement('script');
    s.src = GSI_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Google script'));
    document.head.appendChild(s);
  });
}

/**
 * "Sign in with Google" styled to match the Chunaav design. Google's own button
 * can't be themed, so we render it invisibly on top of our custom button — real
 * Google clicks flow through, and we still receive the ID token (JWT) via
 * `onCredential`. The visible layer is purely presentational.
 */
export default function GoogleLoginButton({
  onCredential,
  disabled,
}: {
  onCredential: (idToken: string) => void;
  disabled?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const gsiRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  // Keep the latest callback without re-initializing GSI on every render.
  const cbRef = useRef(onCredential);
  cbRef.current = onCredential;

  useEffect(() => {
    if (!CLIENT_ID) {
      setError('Google client ID is not configured.');
      return;
    }
    let cancelled = false;
    loadGsi()
      .then(() => {
        const id = window.google?.accounts?.id;
        if (cancelled || !id || !gsiRef.current) return;
        id.initialize({
          client_id: CLIENT_ID,
          callback: (res) => {
            if (res.credential) cbRef.current(res.credential);
          },
        });
        // Match the overlay width to our custom button so the whole thing is clickable.
        const width = Math.min(400, containerRef.current?.offsetWidth || 360);
        id.renderButton(gsiRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          width,
        });
      })
      .catch(() => setError('Could not load Google Sign-In. Check your connection.'));
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return <p className="text-center text-xs text-[#ba1a1a]">{error}</p>;
  }

  return (
    <div
      ref={containerRef}
      className={['group relative h-11 w-full', disabled ? 'pointer-events-none opacity-60' : ''].join(' ')}
    >
      {/* Visible, design-matched layer (non-interactive — clicks hit the overlay). */}
      <button
        type="button"
        tabIndex={-1}
        className="pointer-events-none flex h-11 w-full items-center justify-center gap-3 rounded-lg border border-line bg-white text-sm font-medium text-ink transition-colors group-hover:border-brand/40 group-hover:bg-brand-shade"
      >
        <GoogleIcon className="size-5" />
        Sign in with Google
      </button>

      {/* Google's real button, invisible, layered on top to capture the click. */}
      <div
        ref={gsiRef}
        aria-hidden
        className="absolute inset-0 flex items-center justify-center overflow-hidden opacity-0"
      />
    </div>
  );
}
